'use client'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Query } from '@/lib/generated/graphql-types'
import { SEARCH_SPECIALTY } from '@/modules/surveys/queries'
import {
  convertSalaryFiltersControlsToUrlParams,
  getSalaryFilterControlsFromUrl,
} from '@/modules/salary/filters/filter-utils'
import {
  SalaryFilterControls,
  SalaryFiltersParams,
} from '@/modules/salary/types'
import { SalaryFilterParam } from '../constants'
import { SalaryFilterControl } from '../constants'
import cookies from 'js-cookie'
import { COOKIE_SALARY_FILTER_PARAMS } from '@/modules/salary/filters/utils'
import { isEmpty, omitBy } from 'lodash'
import { GET_USER_PROFILE } from '@/modules/user/profile/queries'
import { PHYSICIAN_SLUG } from '@/modules/surveys/configs/onboarding/personal-info-config'
import { SALARY_FULL_TIME_VALUE } from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import { getSalaryFiltersFromUserProfile } from '@/modules/salary/common/get-default-filters'
import {
  LoadingResults,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import { getSalaryUrl } from '@/modules/salary/filters/get-salary-filters-from-url'
import { usePostHog } from 'posthog-js/react'
import { v4 as uuidv4 } from 'uuid'
import { professionOptions } from '@/modules/surveys/configs/onboarding/personal-info-config'
import { useOccupations } from './use-occupations'
import { useSalaryParams } from '../common/use-salary-params'
import { PostHog } from 'posthog-js'

const trackSalarySearch = (
  filterControls: Partial<SalaryFilterControls> | null,
  updatedFilters: Partial<SalaryFilterControls>,
  searchId: string,
  currentPage: string,
  posthog: PostHog,
  salaryId?: string | null,
) => {
  const professionOption = professionOptions.find(
    (p) => p.slug === filterControls?.profession,
  )

  posthog.capture('searched_salaries', {
    screen_name: 'salary_results',
    profession_name: professionOption?.label,
    specialty_name: filterControls?.specialty || undefined,
    location_name: updatedFilters.state?.label,
    page_id: currentPage,
    search_id: searchId,
    salary_id: salaryId,
  })
}

function saveFiltersToCookies(filters: Partial<SalaryFilterControls>) {
  cookies.set(COOKIE_SALARY_FILTER_PARAMS, JSON.stringify(filters))
}

function updateSalaryFiltersInUrl({
  filtersParams,
  push,
  defaultUserProfessionSlug,
  isTrendsPage,
}: {
  filtersParams: Partial<SalaryFiltersParams>
  push: ReturnType<typeof useRouter>['push']
  defaultUserProfessionSlug: string
  isTrendsPage?: boolean
}) {
  saveFiltersToCookies(filtersParams)
  push(
    getSalaryUrl({
      filtersParams,
      defaultUserProfessionSlug,
      isTrendsPage,
    }),
  )
}

export interface UseSalaryFiltersProps {
  updateSalaryFilters: (
    updatedFilters: Partial<SalaryFilterControls>,
    // loadingResult allows for separate components to control when loading occurs.
    // eg. sortRow component
    options?: { loadingResult?: LoadingResults },
  ) => Promise<void>
  clearSalaryFilters: () => Promise<void>
  filtersCount: number
  loadingResults: LoadingResults | null
  salaryFilterControls: Partial<SalaryFilterControls> | null
  resetLoadingResults: () => void
}
export function useSalaryFilters({
  isAuthenticated,
  countPublicInteraction,
}: {
  isAuthenticated: boolean
  countPublicInteraction?: () => void
}): UseSalaryFiltersProps {
  const searchParams = useSearchParams()
  const { push } = useRouter()
  const { jobType, occupationSlug, location } = useSalaryParams()
  const filtersControls = getSalaryFilterControlsFromUrl({
    searchParams,
    jobType,
    occupationSlug,
    location,
  })
  const hasInitSalaryFilterControlsRef = useRef(false)
  // salaryFilterControls in a useState hook, so that we can initialize it on the server,
  // instead of using zustand (which is client only). We need to initialize it on the server
  // because we need to use the filters to generate the correct page title and meta description
  // for SEO purposes.
  const [salaryFilterControls, setSalaryFilterControls] =
    useState<Partial<SalaryFilterControls> | null>(filtersControls)
  const posthog = usePostHog()
  const pathname = usePathname()
  const isTrendsPage = pathname.includes('/trends')
  const setLoadingResults = useSalaryTableStore(
    (state) => state.setLoadingResults,
  )
  const loadingResults = useSalaryTableStore((state) => state.loadingResults)

  const { getOccupationBySlug, getOccupationByProfessionAndSpecialty } =
    useOccupations()
  const { data: userProfileResponse } = useQuery<Pick<Query, 'getUserProfile'>>(
    GET_USER_PROFILE,
    {
      skip: !isAuthenticated,
    },
  )
  const userProfessionSlug =
    userProfileResponse?.getUserProfile?.occupation?.profession.slug ??
    PHYSICIAN_SLUG

  useEffect(() => {
    // init filters from url in client
    if (hasInitSalaryFilterControlsRef.current) return
    const initFilters = async () => {
      const occupation = occupationSlug
        ? await getOccupationBySlug(occupationSlug)
        : null

      const filters = getSalaryFilterControlsFromUrl({
        searchParams,
        userProfessionSlug,
        jobType,
        occupationSlug,
        location,
        occupation,
      })

      setSalaryFilterControls(filters)
    }
    hasInitSalaryFilterControlsRef.current = true
    initFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateSalaryFilters = useCallback(
    async (
      updatedFilterControls: Partial<SalaryFiltersParams>,
      options?: { loadingResult?: LoadingResults },
    ) => {
      setLoadingResults(options?.loadingResult || LoadingResults.Default)
      countPublicInteraction?.()
      const searchId = uuidv4()
      const currentPage = searchParams.get(SalaryFilterParam.page) ?? '1'
      const salaryId = searchParams.get(SalaryFilterParam.salaryId)
      useSalaryTableStore.getState().setSearchId(searchId)
      trackSalarySearch(
        salaryFilterControls,
        updatedFilterControls,
        searchId,
        currentPage,
        posthog,
        salaryId,
      )
      const newFilterControls = omitBy(
        {
          ...salaryFilterControls,
          ...updatedFilterControls,
        },
        isEmpty,
      )
      const newFilters = await convertSalaryFiltersControlsToUrlParams(
        newFilterControls,
        getOccupationByProfessionAndSpecialty,
      )
      // setState store with new controls, to update the form fields
      setSalaryFilterControls(newFilterControls)
      // update the url to keep browser in sync
      updateSalaryFiltersInUrl({
        filtersParams: newFilters,
        push,
        defaultUserProfessionSlug: userProfessionSlug,
        isTrendsPage,
      })
    },
    [
      setLoadingResults,
      countPublicInteraction,
      searchParams,
      salaryFilterControls,
      posthog,
      getOccupationByProfessionAndSpecialty,
      setSalaryFilterControls,
      push,
      userProfessionSlug,
      isTrendsPage,
    ],
  )

  const clearSalaryFilters = useCallback(async () => {
    const salaryFilters = getSalaryFiltersFromUserProfile({
      userProfile: userProfileResponse?.getUserProfile,
    })
    const clearedFilterControls = {
      [SalaryFilterControl.specialty]: salaryFilters.specialtyId ?? '',
      [SalaryFilterControl.jobType]: SALARY_FULL_TIME_VALUE,
      [SalaryFilterControl.state]: null,
      [SalaryFilterControl.profession]:
        salaryFilters.professionSlug ?? PHYSICIAN_SLUG,
      [SalaryFilterControl.page]: null,
      [SalaryFilterControl.sortBy]: null,
      [SalaryFilterControl.payPeriod]: null,
    }
    const clearedFilters = await convertSalaryFiltersControlsToUrlParams(
      clearedFilterControls,
      getOccupationByProfessionAndSpecialty,
    )
    // setState with new controls, to update the form fields
    setSalaryFilterControls(clearedFilterControls)
    // update the url to keep browser in sync
    updateSalaryFiltersInUrl({
      filtersParams: clearedFilters,
      push,
      defaultUserProfessionSlug: userProfessionSlug,
      isTrendsPage,
    })
  }, [
    userProfileResponse?.getUserProfile,
    getOccupationByProfessionAndSpecialty,
    setSalaryFilterControls,
    push,
    userProfessionSlug,
    isTrendsPage,
  ])

  const resetLoadingResults = useCallback(() => {
    if (loadingResults === null) return
    setLoadingResults(null)
  }, [setLoadingResults, loadingResults])

  const filtersCount = salaryFilterControls
    ? Object.values(salaryFilterControls).filter(Boolean).length
    : 0

  return {
    salaryFilterControls,
    updateSalaryFilters,
    clearSalaryFilters,
    filtersCount,
    loadingResults,
    resetLoadingResults,
  }
}

export function useSelectedSpecialty(opts?: {
  specialtySlug?: string | null | undefined
}) {
  const { specialtySlug } = opts ?? {}
  const { data } = useQuery<Pick<Query, 'searchSpecialty'>>(SEARCH_SPECIALTY, {
    variables: { specialtyInput: { query: '', limit: 200 } },
    skip: !specialtySlug,
  })

  return (
    data?.searchSpecialty?.results.find((s) => s.slug === specialtySlug) ?? null
  )
}
